import React, { useEffect, useState } from 'react';
import Layout from '../../components/Layout';
import API from '../../utils/api';
import { exportToCSV, exportToPDF } from '../../utils/reportsExport';
import toast from 'react-hot-toast';

export default function Reports() {
  const [activeTab, setActiveTab] = useState('stock'); // 'stock' | 'stockLedger' | 'customerLedger' | 'supplierLedger' | 'sales' | 'detailSales' | 'payments'
  
  // Data lists
  const [products, setProducts] = useState([]);
  const [stockLedger, setStockLedger] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [bills, setBills] = useState([]);
  const [paymentLogs, setPaymentLogs] = useState([]);
  const [supplierBills, setSupplierBills] = useState([]);
  const [supplierPayments, setSupplierPayments] = useState([]);
  
  // Customer Ledger specific states
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [selectedCustomerData, setSelectedCustomerData] = useState(null);

  // Supplier Ledger specific states
  const [selectedSupplierName, setSelectedSupplierName] = useState('');
  
  const [search, setSearch] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [loading, setLoading] = useState(true);

  const filterByDate = (dateString) => {
    if (!dateString) return true;
    const itemDate = new Date(dateString);
    if (startDate) {
      const start = new Date(startDate + 'T00:00:00');
      if (itemDate < start) return false;
    }
    if (endDate) {
      const end = new Date(endDate + 'T23:59:59');
      if (itemDate > end) return false;
    }
    return true;
  };

  // Load all reports data
  const loadReportsData = () => {
    setLoading(true);
    Promise.all([
      API.get('/products'),
      API.get('/products/stock-ledger'),
      API.get('/customers'),
      API.get('/bills'),
      API.get('/customers/payments'),
      API.get('/suppliers/bills'),
      API.get('/suppliers/payments')
    ])
      .then(([p, sl, c, b, pay, sb, sp]) => {
        setProducts(p.data);
        setStockLedger(sl.data);
        setCustomers(c.data);
        setBills(b.data);
        setPaymentLogs(pay.data);
        setSupplierBills(sb.data);
        setSupplierPayments(sp.data);
      })
      .catch(() => toast.error('Failed to load reports registry'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadReportsData();
  }, []);

  // Fetch individual customer ledger when selected
  useEffect(() => {
    if (selectedCustomerId) {
      API.get(`/customers/${selectedCustomerId}`)
        .then(r => setSelectedCustomerData(r.data))
        .catch(() => toast.error('Failed to load customer ledger'));
    } else {
      setSelectedCustomerData(null);
    }
  }, [selectedCustomerId]);

  // Clean search query and date filters when tab changes
  useEffect(() => {
    setSearch('');
    setStartDate('');
    setEndDate('');
  }, [activeTab]);

  // Process combined customer ledger items
  const getCustomerLedgerItems = () => {
    if (!selectedCustomerData) return [];
    const ledger = [];
    
    // Process Sales
    (selectedCustomerData.bills || []).forEach(bill => {
      (bill.items || []).forEach(item => {
        const retQty = item.returnQty || item.return_qty || item.returnqty || 0;
        
        // Push sale entry
        ledger.push({
          type: 'SALES',
          date: bill.createdAt,
          detail: item.productName,
          qty: item.quantity,
          rate: item.unitRate,
          total: item.quantity * item.unitRate
        });
        
        // Push return entry if retQty > 0
        if (retQty > 0) {
          ledger.push({
            type: 'RETURN',
            date: bill.createdAt,
            detail: `[RETURN] ${item.productName}`,
            qty: retQty,
            rate: item.unitRate,
            total: -(retQty * item.unitRate)
          });
        }
      });
    });

    // Process Payments
    (selectedCustomerData.payments || []).forEach(pay => {
      ledger.push({
        type: 'PAYMENT',
        date: pay.date,
        detail: pay.mode.toUpperCase(),
        qty: '-',
        rate: pay.amountPaid,
        total: pay.amountPaid
      });
    });

    // Sort chronologically (oldest first)
    return ledger.sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  const customerLedgerItems = getCustomerLedgerItems().filter(item => filterByDate(item.date));

  const getSupplierNames = () => {
    const names = new Set();
    products.forEach(p => { if (p.supplierName) names.add(p.supplierName.trim()); });
    supplierBills.forEach(b => { if (b.supplierName) names.add(b.supplierName.trim()); });
    supplierPayments.forEach(p => { if (p.supplierName) names.add(p.supplierName.trim()); });
    return Array.from(names).sort();
  };
  const supplierNamesList = getSupplierNames();

  const getSupplierBalances = () => {
    const map = {};
    supplierBills.forEach(b => {
      const name = b.supplierName?.trim();
      if (!name) return;
      if (!map[name]) map[name] = { name, invoiced: 0, paid: 0 };
      map[name].invoiced += b.invoiceAmount;
    });
    supplierPayments.forEach(p => {
      const name = p.supplierName?.trim();
      if (!name) return;
      if (!map[name]) map[name] = { name, invoiced: 0, paid: 0 };
      map[name].paid += p.amountPaid;
    });
    return Object.values(map).map(s => ({
      ...s,
      due: parseFloat((s.invoiced - s.paid).toFixed(2))
    }));
  };
  const supplierBalances = getSupplierBalances();

  const getSupplierLedgerItems = () => {
    if (!selectedSupplierName) return [];
    const ledger = [];
    
    supplierBills.filter(b => b.supplierName?.trim().toLowerCase() === selectedSupplierName.toLowerCase()).forEach(b => {
      ledger.push({
        type: 'BILL',
        date: b.invoiceDate,
        detail: `Inv# ${b.invoiceNo}`,
        amount: b.invoiceAmount,
        isPayment: false
      });
    });

    supplierPayments.filter(p => p.supplierName?.trim().toLowerCase() === selectedSupplierName.toLowerCase()).forEach(p => {
      ledger.push({
        type: 'PAYMENT',
        date: p.date,
        detail: p.mode.toUpperCase(),
        amount: p.amountPaid,
        isPayment: true
      });
    });

    ledger.sort((a, b) => new Date(a.date) - new Date(b.date));

    let balance = 0;
    return ledger.map(item => {
      if (item.isPayment) {
        balance -= item.amount;
      } else {
        balance += item.amount;
      }
      return {
        ...item,
        runningBalance: balance
      };
    });
  };
  const supplierLedgerItems = getSupplierLedgerItems().filter(item => filterByDate(item.date));

  // Excel (CSV) and PDF Export Logic
  const handleExport = (format) => {
    let title = '';
    let headers = [];
    let rows = [];
    let filename = `report_${activeTab}_${new Date().toISOString().split('T')[0]}`;

    if (activeTab === 'stock') {
      title = 'Stock Report';
      headers = ['Product Name', 'HSN Code', 'Rack', 'Cost (Rs.)', 'Retail Rate (Rs.)', 'Wholesale Rate (Rs.)', 'GST%', 'Supplier Name', 'Quantity'];
      const filtered = products.filter(p => p.name.toLowerCase().includes(search.toLowerCase()));
      rows = filtered.map(p => [
        p.name,
        p.hsnCode || '-',
        p.rack !== undefined && p.rack !== null ? p.rack : '-',
        p.cost?.toFixed(2) || '0.00',
        p.retailRate?.toFixed(2) || '0.00',
        p.wholesaleRate?.toFixed(2) || '0.00',
        `${p.gst}%`,
        p.supplierName || '-',
        p.quantity
      ]);
    } 
    
    else if (activeTab === 'stockLedger') {
      title = 'Stock Replenishment Ledger';
      headers = ['Received Date', 'Product Name', 'Quantity Received', 'Updated By', 'Supplier'];
      const filtered = stockLedger
        .filter(l => l.productName.toLowerCase().includes(search.toLowerCase()))
        .filter(l => filterByDate(l.receivedDate));
      rows = filtered.map(l => [
        new Date(l.receivedDate).toLocaleDateString('en-IN'),
        l.productName,
        l.quantityReceived,
        l.updatedBy,
        l.supplier
      ]);
    } 
    
    else if (activeTab === 'customerLedger') {
      if (!selectedCustomerData) {
        toast.error('Select a customer first');
        return;
      }
      title = `Customer Ledger: ${selectedCustomerData.name}`;
      headers = ['Type', 'Date', 'Product Name / Mode', 'Quantity', 'Rate / Amount Paid (Rs.)', 'Total (Rs.)'];
      rows = customerLedgerItems.map(item => {
        const isNeg = item.type === 'PAYMENT' || item.type === 'RETURN';
        const rateVal = Math.abs(item.rate);
        const totalVal = Math.abs(item.total);
        return [
          item.type,
          new Date(item.date).toLocaleDateString('en-IN'),
          item.detail,
          item.qty,
          isNeg ? `-${rateVal.toFixed(2)}` : rateVal.toFixed(2),
          isNeg ? `-${totalVal.toFixed(2)}` : totalVal.toFixed(2)
        ];
      });
      filename = `ledger_${selectedCustomerData.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}`;
    } 
    
    else if (activeTab === 'supplierLedger') {
      if (!selectedSupplierName) {
        toast.error('Select a supplier first');
        return;
      }
      title = `Supplier Ledger: ${selectedSupplierName}`;
      headers = ['Type', 'Date', 'Invoice# / Payment Mode', 'Amount Paid / Due (Rs.)', 'Running Balance (Rs.)'];
      rows = supplierLedgerItems.map(item => [
        item.type,
        new Date(item.date).toLocaleDateString('en-IN'),
        item.detail,
        item.type === 'PAYMENT' ? `-${item.amount.toFixed(2)}` : item.amount.toFixed(2),
        item.runningBalance.toFixed(2)
      ]);
      filename = `supplier_ledger_${selectedSupplierName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}`;
    } 
    
    else if (activeTab === 'sales') {
      title = 'Sales Report (Invoices Summary)';
      headers = ['Customer Name', 'Bill Date', 'Bill Number', 'Bill Amount (Rs.)'];
      const filtered = bills
        .filter(b => 
          b.invoiceNo.toLowerCase().includes(search.toLowerCase()) || 
          (b.customer?.name || b.customerName).toLowerCase().includes(search.toLowerCase())
        )
        .filter(b => filterByDate(b.createdAt));
      rows = filtered.map(b => [
        b.customer?.name || b.customerName || 'Unknown',
        new Date(b.createdAt).toLocaleDateString('en-IN'),
        b.invoiceNo,
        b.total.toFixed(2)
      ]);
    } 
    
    else if (activeTab === 'detailSales') {
      title = 'Detail Sales Report';
      headers = ['Bill#', 'Customer Name', 'Date', 'Product Name', 'Quantity', 'Unit Rate (Rs.)', 'GST%', 'Total (Rs.)'];
      
      const flatItems = [];
      bills.forEach(b => {
        (b.items || []).forEach(item => {
          flatItems.push({
            invoiceNo: b.invoiceNo,
            customerName: b.customer?.name || b.customerName || 'Unknown',
            date: b.createdAt,
            productName: item.productName,
            quantity: item.quantity,
            unitRate: item.unitRate,
            gst: item.gst,
            total: item.lineTotal
          });
        });
      });

      const filtered = flatItems
        .filter(item => 
          item.invoiceNo.toLowerCase().includes(search.toLowerCase()) || 
          item.productName.toLowerCase().includes(search.toLowerCase()) ||
          item.customerName.toLowerCase().includes(search.toLowerCase())
        )
        .filter(item => filterByDate(item.date));
      
      rows = filtered.map(item => [
        item.invoiceNo,
        item.customerName,
        new Date(item.date).toLocaleDateString('en-IN'),
        item.productName,
        item.quantity,
        item.unitRate.toFixed(2),
        `${item.gst}%`,
        item.total.toFixed(2)
      ]);
    } 
    
    else if (activeTab === 'payments') {
      title = 'Customer Payments Report';
      headers = ['Customer Name', 'Amount Paid (Rs.)', 'Payment Date', 'Payment Mode'];
      const filtered = paymentLogs
        .filter(p => p.customerName.toLowerCase().includes(search.toLowerCase()))
        .filter(p => filterByDate(p.date));
      rows = filtered.map(p => [
        p.customerName,
        p.amountPaid.toFixed(2),
        new Date(p.date).toLocaleDateString('en-IN'),
        p.mode.toUpperCase()
      ]);
    }

    if (format === 'csv') {
      exportToCSV(headers, rows, `${filename}.csv`);
      toast.success('Excel CSV downloaded');
    } else {
      exportToPDF(title, headers, rows, `${filename}.pdf`);
      toast.success('Printable PDF downloaded');
    }
  };

  return (
    <Layout title="Software Reports Dashboard">
      {/* Tab Select Bar */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 24 }}>
        {[
          { id: 'stock', label: 'Stock Report' },
          { id: 'stockLedger', label: 'Stock Ledger' },
          { id: 'customerLedger', label: 'Customer Ledger' },
          { id: 'supplierLedger', label: 'Supplier Ledger' },
          { id: 'sales', label: 'Sales Report' },
          { id: 'detailSales', label: 'Detail Sales Report' },
          { id: 'payments', label: 'Payment Report' }
        ].map(tab => (
          <button 
            key={tab.id}
            className={`btn ${activeTab === tab.id ? 'btn-primary' : 'btn-outline'}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="card"><div style={{ padding: 60, textAlign: 'center', color: 'var(--gray-400)' }}>Loading data...</div></div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          
          {/* Controls: Search, Dropdowns, Export Buttons */}
          <div className="card">
            <div className="card-body" style={{ display: 'flex', gap: 20, alignItems: 'flex-end', flexWrap: 'wrap', justifyContent: 'space-between' }}>
              
              {activeTab === 'customerLedger' ? (
                <div className="form-group" style={{ flex: 1, minWidth: 260, maxWidth: 320, marginBottom: 0 }}>
                  <label className="form-label">CUSTOMER NAME</label>
                  <select 
                    className="form-control"
                    value={selectedCustomerId}
                    onChange={e => setSelectedCustomerId(e.target.value)}
                  >
                    <option value="">— Select Customer Ledger —</option>
                    {customers.map(c => (
                      <option key={c.id} value={c.id}>{c.name} ({c.phone})</option>
                    ))}
                  </select>
                </div>
              ) : activeTab === 'supplierLedger' ? (
                <div className="form-group" style={{ flex: 1, minWidth: 260, maxWidth: 320, marginBottom: 0 }}>
                  <label className="form-label">SUPPLIER NAME</label>
                  <select 
                    className="form-control"
                    value={selectedSupplierName}
                    onChange={e => setSelectedSupplierName(e.target.value)}
                  >
                    <option value="">— Select Supplier Ledger —</option>
                    {supplierNamesList.map((name, idx) => (
                      <option key={idx} value={name}>{name}</option>
                    ))}
                  </select>
                </div>
              ) : (
                <div className="search-bar" style={{ width: 320 }}>
                  <span className="search-icon">🔍</span>
                  <input 
                    className="form-control" 
                    placeholder="Search columns..." 
                    value={search} 
                    onChange={e => setSearch(e.target.value)}
                    style={{ paddingLeft: 36 }}
                  />
                </div>
              )}

              {activeTab !== 'stock' && (
                <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label" style={{ fontSize: '0.72rem', fontWeight: 600, color: 'var(--gray-600)' }}>START DATE</label>
                    <input 
                      type="date" 
                      className="form-control" 
                      value={startDate} 
                      onChange={e => setStartDate(e.target.value)} 
                      style={{ width: 160 }}
                    />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label" style={{ fontSize: '0.72rem', fontWeight: 600, color: 'var(--gray-600)' }}>END DATE</label>
                    <input 
                      type="date" 
                      className="form-control" 
                      value={endDate} 
                      onChange={e => setEndDate(e.target.value)} 
                      style={{ width: 160 }}
                    />
                  </div>
                  {(startDate || endDate) && (
                    <button 
                      className="btn btn-outline" 
                      style={{ padding: '8px 12px', height: 38, borderColor: 'var(--red)', color: 'var(--red)' }} 
                      onClick={() => { setStartDate(''); setEndDate(''); }}
                    >
                      Clear
                    </button>
                  )}
                </div>
              )}

              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-outline" onClick={() => handleExport('csv')} disabled={(activeTab === 'customerLedger' && !selectedCustomerId) || (activeTab === 'supplierLedger' && !selectedSupplierName)}>
                  📥 Export to Excel (CSV)
                </button>
                <button className="btn btn-primary" onClick={() => handleExport('pdf')} disabled={(activeTab === 'customerLedger' && !selectedCustomerId) || (activeTab === 'supplierLedger' && !selectedSupplierName)}>
                  🖨️ Export / Print PDF
                </button>
              </div>
            </div>
          </div>

          {/* Grid Layout/Table View */}
          <div className="card">
            <div className="table-wrap">
              
              {/* Tab 1: Stock Report */}
              {activeTab === 'stock' && (
                <table>
                  <thead>
                    <tr>
                      <th>Product Name</th>
                      <th>HSN Code</th>
                      <th>Rack</th>
                      <th style={{ textAlign: 'right' }}>Cost</th>
                      <th style={{ textAlign: 'right' }}>Retail Rate</th>
                      <th style={{ textAlign: 'right' }}>Wholesale Rate</th>
                      <th>GST%</th>
                      <th>Supplier Name</th>
                      <th>Quantity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.filter(p => p.name.toLowerCase().includes(search.toLowerCase())).map(p => (
                      <tr key={p.id}>
                        <td><strong style={{ color: 'var(--navy)' }}>{p.name}</strong></td>
                        <td><span className="badge badge-navy" style={{ textTransform: 'none' }}>{p.hsnCode || '—'}</span></td>
                        <td>{p.rack !== undefined && p.rack !== null ? p.rack : '—'}</td>
                        <td style={{ textAlign: 'right', color: 'var(--gray-500)' }}>₹{(p.cost || 0).toFixed(2)}</td>
                        <td style={{ textAlign: 'right', fontWeight: 600, color: 'var(--green)' }}>₹{(p.retailRate || 0).toFixed(2)}</td>
                        <td style={{ textAlign: 'right' }}>₹{(p.wholesaleRate || 0).toFixed(2)}</td>
                        <td>{p.gst}%</td>
                        <td>{p.supplierName || '—'}</td>
                        <td>
                          <span style={{ fontWeight: 600, color: p.quantity < 10 ? 'var(--red)' : 'var(--gray-700)' }}>{p.quantity}</span>
                          {p.quantity < 10 && <span className="badge badge-red" style={{ marginLeft: 6 }}>Low</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {/* Tab 2: Stock Ledger */}
              {activeTab === 'stockLedger' && (
                <table>
                  <thead>
                    <tr>
                      <th>Received Date</th>
                      <th>Product Name</th>
                      <th>Quantity Received</th>
                      <th>Updated By</th>
                      <th>Supplier</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stockLedger
                      .filter(l => l.productName.toLowerCase().includes(search.toLowerCase()))
                      .filter(l => filterByDate(l.receivedDate))
                      .map(l => (
                      <tr key={l.id}>
                        <td>{new Date(l.receivedDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                        <td><strong style={{ color: 'var(--navy)' }}>{l.productName}</strong></td>
                        <td style={{ fontWeight: 600 }}>{l.quantityReceived}</td>
                        <td><span className="badge badge-navy">{l.updatedBy}</span></td>
                        <td>{l.supplier}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {/* Tab 3: Ledger Report (Customer Name Select) */}
              {activeTab === 'customerLedger' && (
                !selectedCustomerId ? (
                  <div className="empty-state" style={{ padding: 40 }}>
                    <div className="empty-icon">👥</div>
                    <div className="empty-title">No customer selected</div>
                    <div className="empty-text">Please choose a customer from the dropdown above to render their financial transactions ledger.</div>
                  </div>
                ) : customerLedgerItems.length === 0 ? (
                  <div className="empty-state" style={{ padding: 40 }}>
                    <div className="empty-icon">🧾</div>
                    <div className="empty-title">Ledger is empty</div>
                    <div className="empty-text">No bills or payments have been recorded for this customer.</div>
                  </div>
                ) : (
                  <>
                    <div style={{ padding: '16px 20px', background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-100)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '0.9rem', color: 'var(--gray-600)' }}>Customer: <strong>{selectedCustomerData?.name}</strong></span>
                      <span style={{ fontSize: '1rem', color: 'var(--navy)' }}>Outstanding Balance Due: <strong style={{ color: selectedCustomerData?.balance > 0 ? 'var(--red)' : 'var(--green)' }}>₹{selectedCustomerData?.balance?.toFixed(2)}</strong></span>
                    </div>
                    <table>
                      <thead>
                        <tr>
                          <th>Type</th>
                          <th>Date</th>
                          <th>Product Name / Mode</th>
                          <th>Quantity</th>
                          <th style={{ textAlign: 'right' }}>Rate / Amount Paid</th>
                          <th style={{ textAlign: 'right' }}>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {customerLedgerItems.map((item, idx) => {
                          const isPayment = item.type === 'PAYMENT';
                          const isReturn = item.type === 'RETURN';
                          
                          let rowBg = '';
                          if (isPayment) rowBg = '#fdf0ee';
                          else if (isReturn) rowBg = 'var(--amber-light)';
                          
                          return (
                            <tr key={idx} style={rowBg ? { background: rowBg } : {}}>
                              <td>
                                <span className={`badge ${isPayment ? 'badge-red' : isReturn ? 'badge-amber' : 'badge-navy'}`}>
                                  {item.type}
                                </span>
                              </td>
                              <td>{new Date(item.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                              <td><strong style={{ color: 'var(--navy)' }}>{item.detail}</strong></td>
                              <td>{item.qty}</td>
                              <td style={{ textAlign: 'right', color: (isPayment || isReturn) ? 'var(--red)' : 'inherit' }}>
                                {(isPayment || isReturn) ? '-' : ''}₹{Math.abs(item.rate).toFixed(2)}
                              </td>
                              <td style={{ textAlign: 'right', fontWeight: 600, color: (isPayment || isReturn) ? 'var(--red)' : 'var(--navy)' }}>
                                {(isPayment || isReturn) ? '-' : ''}₹{Math.abs(item.total).toFixed(2)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </>
                )
              )}

              {/* Tab 3.5: Supplier Ledger */}
              {activeTab === 'supplierLedger' && (
                !selectedSupplierName ? (
                  <div className="empty-state" style={{ padding: 40 }}>
                    <div className="empty-icon">🚚</div>
                    <div className="empty-title">No supplier selected</div>
                    <div className="empty-text">Please choose a supplier from the dropdown above to render their financial transactions ledger.</div>
                  </div>
                ) : supplierLedgerItems.length === 0 ? (
                  <div className="empty-state" style={{ padding: 40 }}>
                    <div className="empty-icon">🧾</div>
                    <div className="empty-title">Ledger is empty</div>
                    <div className="empty-text">No bills or payments have been recorded for this supplier.</div>
                  </div>
                ) : (
                  <>
                    {(() => {
                      const balObj = supplierBalances.find(s => s.name.toLowerCase() === selectedSupplierName.toLowerCase()) || { invoiced: 0, paid: 0, due: 0 };
                      return (
                        <div style={{ padding: '16px 20px', background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-100)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
                          <span style={{ fontSize: '0.9rem', color: 'var(--gray-600)' }}>Supplier: <strong>{selectedSupplierName}</strong></span>
                          <div style={{ display: 'flex', gap: 16, fontSize: '0.85rem', color: 'var(--gray-600)' }}>
                            <span>Total Purchases: <strong>₹{balObj.invoiced.toFixed(2)}</strong></span>
                            <span>Total Paid: <strong>₹{balObj.paid.toFixed(2)}</strong></span>
                            <span style={{ fontSize: '0.95rem', color: 'var(--navy)' }}>Outstanding Due: <strong style={{ color: balObj.due > 0 ? 'var(--red)' : 'var(--green)' }}>₹{balObj.due.toFixed(2)}</strong></span>
                          </div>
                        </div>
                      );
                    })()}
                    <table>
                      <thead>
                        <tr>
                          <th>Type</th>
                          <th>Date</th>
                          <th>Invoice# / Payment Mode</th>
                          <th style={{ textAlign: 'right' }}>Amount Paid / Due</th>
                          <th style={{ textAlign: 'right' }}>Running Due Balance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {supplierLedgerItems.map((item, idx) => (
                          <tr key={idx}>
                            <td>
                              <span className={`badge ${item.type === 'BILL' ? 'badge-navy' : 'badge-green'}`}>
                                {item.type}
                              </span>
                            </td>
                            <td>{new Date(item.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                            <td><strong style={{ color: 'var(--navy)' }}>{item.detail}</strong></td>
                            <td style={{ textAlign: 'right', fontWeight: 600, color: item.type === 'PAYMENT' ? 'var(--green)' : 'var(--navy)' }}>
                              {item.type === 'PAYMENT' ? '-' : ''}₹{item.amount.toFixed(2)}
                            </td>
                            <td style={{ textAlign: 'right', fontWeight: 700, color: item.runningBalance > 0 ? 'var(--red)' : 'var(--green)' }}>
                              ₹{item.runningBalance.toFixed(2)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                )
              )}

              {/* Tab 4: Sales Report */}
              {activeTab === 'sales' && (
                <table>
                  <thead>
                    <tr>
                      <th>Customer Name</th>
                      <th>Bill Date</th>
                      <th>Bill Number</th>
                      <th style={{ textAlign: 'right' }}>Bill Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bills
                      .filter(b => 
                        b.invoiceNo.toLowerCase().includes(search.toLowerCase()) || 
                        (b.customer?.name || b.customerName).toLowerCase().includes(search.toLowerCase())
                      )
                      .filter(b => filterByDate(b.createdAt))
                      .map(b => (
                      <tr key={b.id}>
                        <td><strong style={{ color: 'var(--navy)' }}>{b.customer?.name || b.customerName || 'Unknown'}</strong></td>
                        <td>{new Date(b.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                        <td><span className="badge badge-navy" style={{ textTransform: 'none' }}>{b.invoiceNo}</span></td>
                        <td style={{ textAlign: 'right', fontWeight: 600, color: 'var(--navy)' }}>₹{b.total.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {/* Tab 5: Detailed Sales Report */}
              {activeTab === 'detailSales' && (
                <table>
                  <thead>
                    <tr>
                      <th>Bill#</th>
                      <th>Customer Name</th>
                      <th>Date</th>
                      <th>Product Name</th>
                      <th>Quantity</th>
                      <th style={{ textAlign: 'right' }}>Unit Rate</th>
                      <th>GST%</th>
                      <th style={{ textAlign: 'right' }}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const flatItems = [];
                      bills.forEach(b => {
                        (b.items || []).forEach(item => {
                          flatItems.push({
                            id: item.id,
                            invoiceNo: b.invoiceNo,
                            customerName: b.customer?.name || b.customerName || 'Unknown',
                            date: b.createdAt,
                            productName: item.productName,
                            quantity: item.quantity,
                            unitRate: item.unitRate,
                            gst: item.gst,
                            total: item.lineTotal
                          });
                        });
                      });

                      return flatItems
                        .filter(item => 
                          item.invoiceNo.toLowerCase().includes(search.toLowerCase()) || 
                          item.productName.toLowerCase().includes(search.toLowerCase()) ||
                          item.customerName.toLowerCase().includes(search.toLowerCase())
                        )
                        .filter(item => filterByDate(item.date))
                        .map(item => (
                        <tr key={item.id}>
                          <td><span className="badge badge-navy" style={{ textTransform: 'none' }}>{item.invoiceNo}</span></td>
                          <td><strong style={{ color: 'var(--navy)' }}>{item.customerName}</strong></td>
                          <td>{new Date(item.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                          <td>{item.productName}</td>
                          <td>{item.quantity}</td>
                          <td style={{ textAlign: 'right' }}>₹{item.unitRate.toFixed(2)}</td>
                          <td>{item.gst}%</td>
                          <td style={{ textAlign: 'right', fontWeight: 600, color: 'var(--navy)' }}>₹{item.total.toFixed(2)}</td>
                        </tr>
                      ));
                    })()}
                  </tbody>
                </table>
              )}

              {/* Tab 6: Payment Report */}
              {activeTab === 'payments' && (
                <table>
                  <thead>
                    <tr>
                      <th>Customer Name</th>
                      <th style={{ textAlign: 'right' }}>Amount Paid</th>
                      <th>Payment Date</th>
                      <th>Payment Mode</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paymentLogs
                      .filter(p => p.customerName.toLowerCase().includes(search.toLowerCase()))
                      .filter(p => filterByDate(p.date))
                      .map(p => (
                      <tr key={p.id}>
                        <td><strong style={{ color: 'var(--navy)' }}>{p.customerName}</strong></td>
                        <td style={{ textAlign: 'right', fontWeight: 600, color: 'var(--green)' }}>₹{p.amountPaid.toFixed(2)}</td>
                        <td>{new Date(p.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                        <td>
                          <span className={`badge ${p.mode === 'cash' ? 'badge-green' : p.mode === 'card' ? 'badge-navy' : 'badge-amber'}`}>
                            {p.mode.toUpperCase()}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

            </div>
          </div>

        </div>
      )}
    </Layout>
  );
}
